/**
 * Client for the MONALISA LIVING Laravel API.
 *
 * The site is served by Laravel itself, so every call is same-origin — no CORS
 * pre-flights and no configuration. The home-page content is inlined into the
 * document as `window.__MONALISA__`, so a normal visit makes **zero** API calls;
 * the fetch helpers below are the fallback used when that payload is absent
 * (e.g. the CRA dev server) and by the account panel.
 */

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const TOKEN_KEY = 'monalisa-account-token';

const inlined = (typeof window !== 'undefined' && window.__MONALISA__) || null;

/** Content rendered on first paint, already in the document. `null` when unavailable. */
export const bootstrapSections = inlined && inlined.sections && inlined.sections.length
  ? inlined.sections
  : null;

export const bootstrapProducts = inlined && Array.isArray(inlined.products)
  ? inlined.products
  : null;

export const bootstrapPages = inlined && Array.isArray(inlined.pages)
  ? inlined.pages
  : null;

/** Header "Products" menu (columns + image cards), admin-managed. `null` when unavailable. */
export const bootstrapMegaMenu = inlined && inlined.megaMenu && inlined.megaMenu.columns
  ? inlined.megaMenu
  : null;


/* ------------------------------------------------------------------ token */

export function getToken() {
  try {
    return window.localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function setToken(token) {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private mode — the session stays in memory only */
  }
}

/* -------------------------------------------------------------- transport */

async function request(path, options = {}) {
  const response = await fetch(API_BASE + path, {
    ...options,
    headers: {
      Accept: 'application/json',
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.auth ? { Authorization: 'Bearer ' + getToken() } : {}),
      ...options.headers,
    },
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    // Laravel validation errors arrive as { message, errors: { field: [msg] } }.
    const first = payload.errors && Object.values(payload.errors)[0];
    throw new Error(
      (Array.isArray(first) ? first[0] : null) ||
      payload.message ||
      'Request failed (' + response.status + ')'
    );
  }

  return payload;
}

/* -------------------------------------------------------------- content */

export async function fetchSections() {
  if (bootstrapSections) return bootstrapSections;
  const { sections } = await request('/api/sections');
  return sections || [];
}

export async function fetchProducts() {
  if (bootstrapProducts) return bootstrapProducts;
  const { products } = await request('/api/products');
  return products || [];
}

export async function fetchMegaMenu() {
  if (bootstrapMegaMenu) return bootstrapMegaMenu;
  const { megaMenu } = await request('/api/mega-menu');
  return megaMenu || { columns: [], promos: [] };
}

export async function fetchPages() {
  if (bootstrapPages) return bootstrapPages;
  const { pages } = await request('/api/pages');
  return pages || [];
}

export async function createOrder(values) {
  const { order } = await request('/api/orders', { method: 'POST', body: JSON.stringify(values) });
  return order;
}

export async function fetchOrder(number, token) {
  const { order } = await request(`/api/orders/${encodeURIComponent(number)}?token=${encodeURIComponent(token)}`);
  return order;
}

/* -------------------------------------------------------------- account */

export async function registerAccount(values) {
  const payload = await request('/api/account/register', {
    method: 'POST',
    body: JSON.stringify(values),
  });
  setToken(payload.token);
  return payload.account;
}

export async function loginAccount(email, password) {
  const payload = await request('/api/account/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  setToken(payload.token);
  return payload.account;
}

export async function fetchAccount() {
  if (!getToken()) return null;
  try {
    const { account } = await request('/api/account/me', { auth: true });
    return account;
  } catch {
    setToken('');
    return null;
  }
}

export async function updateAccount(values) {
  const { account } = await request('/api/account/profile', {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(values),
  });
  return account;
}

export async function logoutAccount() {
  try {
    if (getToken()) await request('/api/account/logout', { method: 'POST', auth: true });
  } finally {
    setToken('');
  }
}
